/**
 * World reconstruction hooks.
 *
 * useWorldAt(t)   memoised `buildWorldState(record, t)`, recomputed at most once per animation
 *                 frame no matter how often `t` or the record change.
 * useLiveWorld()  world at serverNow(), updated ~5 Hz (configurable 1–15 Hz).
 *
 * Both return null until the record is loaded, and if the shared reconstruction throws (e.g. the
 * shared lane is still a stub) — the UI must degrade, never crash.
 */
import { useEffect, useRef, useState } from 'react';
import { buildWorldState, type EpochMs, type MissionRecord, type WorldOptions, type WorldState } from '@apos/shared';
import { serverNow } from '../lib/clock';
import { useMission } from './mission';

let warned = false;
export function computeWorld(record: MissionRecord | null, t: EpochMs | null, opts?: WorldOptions): WorldState | null {
  if (!record || t === null || !Number.isFinite(t)) return null;
  try {
    return buildWorldState(record, t, opts);
  } catch (e) {
    if (!warned) {
      warned = true;
      console.warn('[world] buildWorldState unavailable:', (e as Error).message);
    }
    return null;
  }
}

/** World at an arbitrary time `t` from the mission store's record (or an explicit record). */
export function useWorldAt(t: EpochMs | null, opts?: { record?: MissionRecord | null; world?: WorldOptions }): WorldState | null {
  const storeRecord = useMission((s) => s.record);
  const record = opts?.record !== undefined ? opts.record : storeRecord;
  const [world, setWorld] = useState<WorldState | null>(() => computeWorld(record, t, opts?.world));
  const latest = useRef({ record, t, wopts: opts?.world });
  latest.current = { record, t, wopts: opts?.world };
  const raf = useRef(0);
  const last = useRef<{ record: MissionRecord | null; t: EpochMs | null }>({ record, t });

  useEffect(() => {
    if (last.current.record === record && last.current.t === t && world) return;
    if (raf.current) return;
    raf.current = requestAnimationFrame(() => {
      raf.current = 0;
      const { record: r, t: tt, wopts } = latest.current;
      last.current = { record: r, t: tt };
      setWorld(computeWorld(r, tt, wopts));
    });
  }, [record, t, world]);

  useEffect(() => () => cancelAnimationFrame(raf.current), []);
  return world;
}

/** Server-now ticking at `hz` (1–15). */
export function useLiveTime(hz = 5): EpochMs {
  const [t, setT] = useState(() => serverNow());
  useEffect(() => {
    const period = 1000 / Math.min(15, Math.max(1, hz));
    const id = setInterval(() => setT(serverNow()), period);
    return () => clearInterval(id);
  }, [hz]);
  return t;
}

/** World at server-now, ~`hz` updates per second. */
export function useLiveWorld(hz = 5, opts?: { world?: WorldOptions }): WorldState | null {
  const t = useLiveTime(hz);
  return useWorldAt(t, opts);
}
